import React from 'react';

import { ALL_EXPERTS_COMPONENT_NAME } from '@/components/ui/AllExperts/AllExperts';
import { BREADCRUMB_COMPONENT_NAME } from '@/components/ui/Breadcrumbs/Breadcrumbs';
import {
  FEATURED_ARTICLES_DEFAULT_LIMIT,
  FEATURED_ARTICLES_NAME,
} from '@/components/ui/FeaturedArticles/FeaturedArticles.types';
import {
  ARTICLE_SIDEBAR_NAME,
  ARTICLE_SIDEBAR_DEFAULT_LIMIT,
} from '@/components/ui/ArticleSidebar/ArticleSidebar.types';
import { ARTICLES_LIST_NAME } from '@/components/ui/ArticlesList/ArticlesList.types';
import { HELP_CENTRE_LIST_NAME } from '@/components/ui/HelpCentreList/HelpCentreList.types';
import { HUB_FEATURED_ARTICLES_NAME } from '@/components/ui/HubFeaturedArticles/HubFeaturedArticles';
import { HUB_NEWS_CAROUSEL_NAME } from '@/components/ui/HubNewsCarousel/HubNewsCarousel';
import {
  getAllExperts,
  getAllHelpCentrePreviews,
  getAllArticlePreviews,
  getArticle,
  getAuthor,
  getLatestArticlePreviews,
  getHelpCentrePage,
  getNavigation,
  getFooter,
  getPage,
  getTag,
  verifyCampaign,
  getNewsRoomArticle,
  getGuideArticle,
  getNewsArticle,
} from '@/data/api';
import { populateBreadcrumbFromComponent } from '@/data/breadcrumb';
import { getAllBlocksWithComponentName } from '@/data/builder';
import { ArticlePreview, ArticlePreviewWithPagination, BuilderArticlePage } from '@/types/article';
import { Author } from '@/types/author';
import { BreadcrumbItem } from '@/types/breadcrumb';
import { CampaignState } from '@/types/campaign';
import { HelpCentrePreview } from '@/types/helpCentre';
import { NavigationItem } from '@/types/navigation';
import { builder, BuilderContent } from '@builder.io/sdk';
import { notFound } from 'next/navigation';
import generateSchema from '@/utils/schema';
import { FooterData } from '@/types/footer';
import { host } from '@/config';
import { BuilderUrlProvider } from '@/components/BuilderUrlContext';
import { frontCoverPageSize } from '@/config';

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

export const BUILDER_PAGE_MODELS = {
  page: 'page',
  article: 'article',
  author: 'author-page',
  tag: 'tag',
  category: 'category',
  helpCentre: 'help-centre',
  newsRoom: 'news-room',
};

export const BUILDER_DATA_MODELS = {
  banner: 'banner',
  siteAlert: 'site-alert',
};

// Must sync up with the "name" enum https://builder.io/models/e935df35d1674d0cae6d0309d7da1108
export const BANNER_TYPES = {
  appDownload: 'App Download',
  cookie: 'Cookie',
  campaign: 'Campaign',
};

export type BannerType = (typeof BANNER_TYPES)[keyof typeof BANNER_TYPES];

export type BuilderCustomState = {
  articles?: ArticlePreviewWithPagination & {
    clientSideFiltering?: boolean;
    pageSize?: number;
  };
  featuredArticles: ArticlePreview[];
  breadcrumbs: BreadcrumbItem[] | null;
  breadcrumbPageTitle: string | null;
  experts: Author[];
  helpCentrePages: HelpCentrePreview[];
};

type PagePropParams = {
  // This matches the dynamic route in the file name [...page]
  page: string[];
};

export interface PageProps {
  params: Promise<PagePropParams>;
  searchParams: Promise<{
    [key: string]: string | string[];
  }>;
}

export interface BuilderPageData<P> {
  // Is optional because of 404 or draft/preview pages.
  content?: P;
  params?: PagePropParams;
  searchParams?: Record<string, string | string[] | undefined>;
  jsonLd: unknown;
  state: BuilderCustomState;
  navigation: NavigationItem[];
  footer: FooterData;
  campaignInfo: CampaignState | null;
  url: string;
}

export function withBuilderPageData<P extends BuilderContent | BuilderArticlePage>(
  WrappedComponent: React.ComponentType<BuilderPageData<P>>,
  options: {
    builderModelName: string;
    // Allows overriding the URL used to fetch the Builder content.
    builderUrl?: string;
    // URL prefix for content lookup, determines which fetch function to use.
    urlPrefix?: 'guides' | 'news';
  }
) {
  return async function BuilderWrappedPage(props: PageProps) {
    const params = await props.params;
    const searchParams = await props.searchParams;

    let url = options.builderUrl ?? (params?.page?.join('/') || '');

    const isPaginated = params?.page?.at(-2) == 'page' && /\d+/.test(params?.page?.at(-1) ?? '');
    const currentPageNumber = isPaginated ? parseInt(params!.page.at(-1)!, 10) : 1;

    if (isPaginated) {
      url = params?.page?.slice(0, -2).join('/');
    }

    // Helper to get the appropriate content fetch based on model type
    const getContentForModel = () => {
      switch (options.builderModelName) {
        case BUILDER_PAGE_MODELS.article:
          if (options.urlPrefix === 'guides') {
            return getGuideArticle(url, searchParams);
          } else if (options.urlPrefix === 'news') {
            return getNewsArticle(url, searchParams);
          } else {
            return getArticle(url, searchParams);
          }
        case BUILDER_PAGE_MODELS.newsRoom:
          return getNewsRoomArticle(url, searchParams);
        case BUILDER_PAGE_MODELS.tag:
          return getTag(url, searchParams);
        case BUILDER_PAGE_MODELS.author:
          return getAuthor(url, searchParams);
        case BUILDER_PAGE_MODELS.helpCentre:
          return getHelpCentrePage(url, searchParams);
        default:
          return getPage(url, searchParams);
      }
    };

    // Parallelize content fetch with navigation/footer/campaign for better TTFB
    // These fetches are independent and don't depend on each other
    const utmCampaignRef = searchParams?.utm_campaignref as string;
    const [content, navigation, footer, campaignInfo] = await Promise.all([
      getContentForModel(),
      getNavigation(),
      getFooter(),
      utmCampaignRef ? verifyCampaign(utmCampaignRef) : Promise.resolve(null),
    ]);

    // Return 404 if no content, guard against category pages throwing 404
    if (!content && options.builderModelName !== BUILDER_PAGE_MODELS.article) {
      return notFound();
    }

    /*
      Get the blocks from Builder.
    */
    const featuredArticlesBlocks = getAllBlocksWithComponentName(content, FEATURED_ARTICLES_NAME);
    const hubFeaturedArticlesBlocks = getAllBlocksWithComponentName(content, HUB_FEATURED_ARTICLES_NAME);
    const hubNewsCarouselBlocks = getAllBlocksWithComponentName(content, HUB_NEWS_CAROUSEL_NAME);
    const articleSidebarBlocks = getAllBlocksWithComponentName(content, ARTICLE_SIDEBAR_NAME);
    const breadcrumbBlocks = getAllBlocksWithComponentName(content, BREADCRUMB_COMPONENT_NAME);
    const allExpertsBlocks = getAllBlocksWithComponentName(content, ALL_EXPERTS_COMPONENT_NAME);

    const articlesListBlocks = getAllBlocksWithComponentName(content, ARTICLES_LIST_NAME);

    const pageContainsFeaturedArticles =
      featuredArticlesBlocks.length > 0 || hubFeaturedArticlesBlocks.length > 0 || hubNewsCarouselBlocks.length > 0;
    const pageContainsArticleSidebar = articleSidebarBlocks.length > 0;
    const pageContainsArticlesList = articlesListBlocks.length > 0;
    const pageContainsAllExperts = allExpertsBlocks.length > 0;
    const helpCentreListBlocks = getAllBlocksWithComponentName(content, HELP_CENTRE_LIST_NAME);
    const pageContainsHelpCentreList = helpCentreListBlocks.length > 0;

    /*
      Check if we need to load latest articles.
      Always fetch if the page has a FeaturedArticles or ArticleSidebar block,
      so the data is available when toggling between manual and auto-select modes
      in the Builder editor. Determine the maximum limit needed across all blocks.
    */
    let maxArticleLimit = 0;
    [
      ...featuredArticlesBlocks,
      ...hubFeaturedArticlesBlocks,
      ...hubNewsCarouselBlocks,
      ...articleSidebarBlocks,
    ].forEach((block) => {
      const autoSelectLatest = block?.component?.options?.autoSelectLatest;
      if (autoSelectLatest !== false) {
        const defaultLimit =
          block?.component?.name === ARTICLE_SIDEBAR_NAME
            ? ARTICLE_SIDEBAR_DEFAULT_LIMIT
            : FEATURED_ARTICLES_DEFAULT_LIMIT;
        const limit = block?.component?.options?.limit ?? defaultLimit;
        maxArticleLimit = Math.max(maxArticleLimit, limit);
      }
    });

    const shouldLoadFeaturedArticles = pageContainsFeaturedArticles || pageContainsArticleSidebar;

    const articlePageModel =
      options.builderUrl === 'newsroom' ? BUILDER_PAGE_MODELS.newsRoom : BUILDER_PAGE_MODELS.article;

    const articlesListPromise = pageContainsArticlesList
      ? (async () => {
          const allArticles = await getAllArticlePreviews({ pageModel: articlePageModel });
          return {
            articles: allArticles,
            currentPage: currentPageNumber,
            totalPages: Math.max(1, Math.ceil(allArticles.length / frontCoverPageSize)),
            limit: frontCoverPageSize,
          } as ArticlePreviewWithPagination;
        })()
      : Promise.resolve(null);

    // Parallelize conditional data fetches for better performance
    const [featuredArticlesResult, articlesListResult, experts, helpCentrePages] = await Promise.all([
      shouldLoadFeaturedArticles
        ? getLatestArticlePreviews(maxArticleLimit || FEATURED_ARTICLES_DEFAULT_LIMIT, articlePageModel)
        : Promise.resolve(null),
      articlesListPromise,
      pageContainsAllExperts ? getAllExperts() : Promise.resolve([] as Author[]),
      pageContainsHelpCentreList ? getAllHelpCentrePreviews() : Promise.resolve([] as HelpCentrePreview[]),
    ]);

    const featuredArticles = featuredArticlesResult?.articles ?? ([] as ArticlePreview[]);
    const articles = articlesListResult
      ? {
          ...articlesListResult,
          urlPrefix: `${host}/${url}`,
          clientSideFiltering: true,
          pageSize: frontCoverPageSize,
        }
      : undefined;

    /*
      Assuming there is only one breadcrumb block per page.
      Extract both the breadcrumb trail AND the pageTitle from the component options.
    */
    const breadcrumbBlock = breadcrumbBlocks[0];
    const breadcrumbs = breadcrumbBlock ? await populateBreadcrumbFromComponent(breadcrumbBlock) : null;
    const breadcrumbPageTitle = breadcrumbBlock?.component?.options?.pageTitle || null;

    const jsonLd = generateSchema({ content: content, url: url, model: options.builderModelName });

    return (
      <BuilderUrlProvider url={url}>
        <WrappedComponent
          content={content as P}
          params={params}
          searchParams={searchParams}
          jsonLd={jsonLd}
          navigation={navigation}
          footer={footer}
          campaignInfo={campaignInfo}
          url={url}
          state={{
            articles,
            featuredArticles,
            breadcrumbs,
            breadcrumbPageTitle,
            experts,
            helpCentrePages,
          }}
        />
      </BuilderUrlProvider>
    );
  };
}
